import OrderInspector from '@/features/orders/components/OrderInspector';
import OrderListView from '@/features/orders/views/OrderListView';
import OrderPauseCard from '@/features/orders/components/OrderPauseCard';
import OrderSummaryBar from '@/features/orders/components/OrderSummaryBar';
import WorkspaceCanvas from '@/components/WorkspaceCanvas';
import { type StatusChip } from '@/components/StatusFilterChips';
import { orderStatusColor } from '@/features/orders/utils/order-status';
import { todayString } from '@/utils/datetime';
import { useT } from '@/i18n';
import { useCan } from '@/hooks/useAbility';
import { useBottomBarInset } from '@/utils/useBottomBarInset';
import { sectionRouter } from '@/utils/navigateMain';
import { useSupportsDockedDetail } from '@/utils/useMediaQuery';
import type { OnlineOrder, OrderStatus } from '@restaurant/api-client';
import { useQueryClient } from '@tanstack/react-query';
import {
  Navbar,
  NavLeft,
  NavTitle,
  NavRight,
  Page,
  Subnavbar,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { useEffect, useState } from 'react';

type OrderStatusFilter = OrderStatus | 'ALL';

/**
 * Orders workspace - the dashboard's order-management surface. Mirrors the
 * reservations workspace exactly: a single list fills the canvas, selecting an
 * order opens the inspector (docked on wide layouts, pushed as a page on
 * phones).
 *
 * No inline filter chrome - same call the reservations workspace makes. The
 * operator triages off the status badge on each card; a pickup/delivery filter
 * is noise (every order is handled regardless of type, and type is already a
 * glyph on the card). "Needs action" belongs in a dedicated entry later, the
 * way reservations does its pending queue - not a subnavbar of chips.
 */
export default function OrdersWorkspacePage() {
  // Dock the order inspector only where there's room beside the app nav sidebar
  // and master list; below this breakpoint (e.g. iPad mini landscape) the order
  // detail pushes as a full page instead - see useSupportsDockedDetail.
  const t = useT();
  const canSeeAwaitingPayment = useCan('manage', 'OnlineOrder');
  const canDockDetail = useSupportsDockedDetail();
  const bottomBarInset = useBottomBarInset();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>('ALL');
  // Scope the board to the restaurant's local today. `todayString()` resolves in
  // Settings.defaultZone (restaurant tz, set in BootstrapGate); the server turns
  // this YYYY-MM-DD into that timezone's day bounds and filters by each order's
  // service instant (scheduledFor → estimatedReadyAt → createdAt).
  const filters = {
    day: todayString(),
    ...(statusFilter === 'ALL' ? {} : { status: statusFilter }),
  };

  // Quick status filter for the main list - the operator asked to narrow by
  // status without opening the history page. 'ALL' clears the filter (server
  // returns every status, the list re-sections them).
  const STATUS_CHIPS: StatusChip<OrderStatusFilter>[] = [
    { key: 'ALL', label: t.orders.all },
    { key: 'PENDING', label: t.orders.status.PENDING, color: orderStatusColor.PENDING },
    { key: 'CONFIRMED', label: t.orders.status.CONFIRMED, color: orderStatusColor.CONFIRMED },
    { key: 'COMPLETED', label: t.orders.status.COMPLETED, color: orderStatusColor.COMPLETED },
    { key: 'CANCELLED', label: t.orders.status.CANCELLED, color: orderStatusColor.CANCELLED },
  ];

  // Pull-to-refresh - the orders workspace is always a single list, so refetch
  // every orders query (list + pending-count + summary) on a pull, plus the
  // backend-computed channel status that backs the availability strip (it already
  // folds in enabled/pause/hours), so the banner refreshes with the list.
  const queryClient = useQueryClient();
  const onRefresh = () =>
    Promise.all([
      queryClient.refetchQueries({ queryKey: ['orders'] }),
      // Just the channel-status leaf (['order-setup', restaurantId, 'channel-status'])
      // - not the whole order-setup domain (shifts/holidays/config live on Settings).
      queryClient.refetchQueries({
        predicate: (q) => q.queryKey[0] === 'order-setup' && q.queryKey[2] === 'channel-status',
      }),
    ]);

  // Drop a docked selection when narrowing below the docking breakpoint -
  // otherwise a selected card lingers with no panel visible.
  useEffect(() => {
    if (!canDockDetail && selectedId) setSelectedId(null);
  }, [canDockDetail, selectedId]);

  const handleSelect = (order: OnlineOrder) => {
    if (canDockDetail) {
      setSelectedId(order.id);
    } else {
      // `sectionRouter()`, NOT `f7.views.current`: between 768px and the docking
      // breakpoint (iPad) the app runs the DESKTOP shell, whose single `<View>`
      // sits inside `.app-desktop-layout > .app-desktop-main` with no `.views`
      // wrapper. F7's `getCurrentView` only looks at DIRECT children of `.views`
      // (falling back to the app root), so it finds nothing and returns
      // `undefined` — `.router` on it threw and the tap did nothing at exactly
      // the widths where the inspector isn't docked either. `sectionRouter()`
      // picks `views.main` on desktop and the active tab's view on mobile, so
      // Orders still pushes onto its OWN stack there (a hardcoded `views.main`
      // would push the detail onto the Reservations tab).
      sectionRouter()?.navigate(`/orders/${order.id}`);
    }
  };

  // Full-height shell (`pageContent={false}` + WorkspaceCanvas): the list and the
  // docked inspector are each their own scroller (`.rsv-main` / `.rsv-inspector-scroll`),
  // so they scroll INDEPENDENTLY instead of moving the whole page. The scrollers'
  // top padding clears the navbar + summary subnavbar via `--rsv-nav-offset`
  // (defined for `[data-name='orders']` in styles.css).
  // Mobile status filter: an icon button in the navbar that opens an action-sheet
  // picker; the active filter is surfaced by a content banner (not the button).
  const openStatusPicker = (targetEl: HTMLElement) => {
    f7.actions
      .create({
        // Anchor to the trigger so on regular-width (iPad/desktop) F7 converts
        // the sheet to a popover that closes on outside click; without a target
        // it stays a bottom sheet even on desktop and the backdrop dismiss is
        // unreliable.
        targetEl,
        buttons: [
          STATUS_CHIPS.map((c) => ({
            text: c.label,
            bold: c.key === statusFilter,
            onClick: () => setStatusFilter(c.key),
          })),
        ],
      })
      .open();
  };

  const filterBanner =
    statusFilter !== 'ALL'
      ? (() => {
          const chip = STATUS_CHIPS.find((c) => c.key === statusFilter);
          return (
            <div className="wsp-filter-banner">
              {chip?.color && (
                <span className="wsp-filter-dot" style={{ background: chip.color }} />
              )}
              <span className="wsp-filter-banner-label">{chip?.label}</span>
            </div>
          );
        })()
      : null;

  const main = (
    <>
      <OrderPauseCard />
      {filterBanner}
      <OrderListView filters={filters} selectedId={selectedId} onSelectOrder={handleSelect} />
    </>
  );

  return (
    <Page name="orders" pageContent={false}>
      <Navbar>
        <NavLeft>
          <button
            type="button"
            className={`wsp-filter-trigger${statusFilter !== 'ALL' ? ' is-active' : ''}`}
            onClick={(e) => openStatusPicker(e.currentTarget)}
            aria-label={t.orders.filter}
          >
            <i className="f7-icons">line_horizontal_3_decrease</i>
          </button>
        </NavLeft>
        <NavTitle>{t.orders.page.title}</NavTitle>
        <NavRight>
          {canSeeAwaitingPayment && (
            <F7Link
              href="/orders/awaiting-payment"
              iconF7="hourglass"
              tooltip={t.orders.awaitingPayment.tooltip}
            />
          )}
          <F7Link href="/orders/history" iconF7="archivebox" tooltip={t.orders.history.tooltip} />
        </NavRight>
        <Subnavbar>
          <OrderSummaryBar />
        </Subnavbar>
      </Navbar>

      <WorkspaceCanvas
        isWide={canDockDetail}
        bottomBarInset={bottomBarInset}
        onRefresh={onRefresh}
        main={main}
        // Orders keeps the panel docked at all times - the inspector renders its
        // own "No order selected" empty state, so it's always a truthy node.
        inspector={<OrderInspector surface="docked" orderId={selectedId} />}
      />
    </Page>
  );
}
